/*
 * /dev/atom-loader — internal preview route so the founder can eyeball
 * the AtomLoader across sizes + reduced-motion + message-cycling without
 * needing to trigger a real generate-and-wait flow. Not linked from any
 * user-facing surface.
 *
 * Jun 2026: updated to showcase the shared, stage-neutral generator
 * loader pool (`GENERATOR_LOADER_MESSAGES`) — the copy every real
 * generate-and-wait now cycles. The old per-generator stage arrays are
 * gone from the product, so they are gone from here too. Only the two
 * still-live single-message labels remain (slide preview + Path A image
 * generation).
 */
import React from "react";
import {
  AtomLoader,
  ATOM_MESSAGES,
  GENERATOR_LOADER_MESSAGES,
} from "../components/AtomLoader";
import GenerationLoader from "../components/math-engine/GenerationLoader";

export default function AtomLoaderPreview() {
  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(120% 90% at 50% 0%, #FBFDFE 0%, #EDF3F6 60%, #E7EEF2 100%)",
        padding: "40px 20px",
        fontFamily: "'Montserrat', system-ui, sans-serif",
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <h1 style={{ fontSize: 22, color: "#22324B", marginBottom: 8 }}>
          AtomLoader preview
        </h1>
        <p style={{ fontSize: 13, color: "#6C7A90", marginBottom: 32 }}>
          The shared generator pool below is what every generate-and-wait
          in the product now cycles — shuffled per generation, rotating on
          a 6s timer, looping indefinitely with no progress claim.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 32,
            justifyItems: "center",
          }}
        >
          <div>
            <Caption>Shared generator pool (md · shuffled cycle)</Caption>
            <AtomLoader messages={GENERATOR_LOADER_MESSAGES} />
          </div>
          <div>
            <Caption>Shared generator pool (sm · First Run panel)</Caption>
            <AtomLoader size="sm" messages={GENERATOR_LOADER_MESSAGES} />
          </div>
          <div>
            <Caption>Slide preview (sm · shared pool — now live)</Caption>
            <AtomLoader
              size="sm"
              messages={GENERATOR_LOADER_MESSAGES}
              sublabel="First run can take ~20s."
            />
          </div>
          <div>
            <Caption>Lesson images (single msg · Path A, not yet wired)</Caption>
            <AtomLoader
              message={ATOM_MESSAGES.lessonImages.message}
              sublabel={ATOM_MESSAGES.lessonImages.sublabel}
            />
          </div>
        </div>
        {/* Math Engine wait screen — full-card treatment. The heartbeat
            line now draws from the shared pool; the REAL backend progress
            waypoints and the fixed stage preamble (label/title/body) are
            untouched. */}
        <h2 style={{ fontSize: 18, color: "#22324B", margin: "40px 0 12px" }}>
          Math Engine — full-card generation wait
        </h2>
        <div style={{ maxWidth: 780, margin: "0 auto" }}>
          <GenerationLoader
            stage="lesson1"
            mode="continue"
            unit={{
              grade: "Grade 5",
              unit_number: 3,
              name: "Fractions with Unlike Denominators",
            }}
            mini={{ label: "Mini-unit 3B", name: "Common Denominators" }}
          />
        </div>
      </div>
    </div>
  );
}

const Caption = ({ children }) => (
  <p
    style={{
      textAlign: "center",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "1.4px",
      textTransform: "uppercase",
      color: "rgba(34,50,75,0.5)",
      marginBottom: 12,
    }}
  >
    {children}
  </p>
);
